import type { MovementKind, Product, SalesFigures, WriteOffReason } from '@/api/goods'
import type { Branch } from '@/api/types'
import type { Caller } from './abilities'
import { parsePrice } from './pass-rules'
import { reportZoneOf } from './periods'
import { atLeast, effectiveLimits } from './permissions'
import type { Shelf } from './shelves'

/// «Товари» — what the section shows and what a paper means, pure.
/// SPEC: specs/shop.md, specs/roles.md §3 (module `stock`).

// ---- who does what ----

/// The server's rules, mirrored — never one of the panel's own: a button the
/// server would refuse is not drawn, and nothing the server allows is hidden
/// (specs/shop.md §4, roles.md R8).
export type GoodsAbilities = Readonly<{
  /// The section, as a shelf: `stock ≥ view` — or a desk that sells
  /// (`sales ≥ edit`): whoever sells water must see how much is left.
  see: boolean
  /// What goods cost — purchase prices, the shelf's worth, margins:
  /// `stock ≥ view`. Below it the server leaves the cost fields out.
  costs: boolean
  /// The papers (receipts, write-offs, sales) and a product's movements:
  /// `stock ≥ view`, never scope `own` — attached to no paper, it reads none.
  papers: boolean
  /// A goods sale: `sales ≥ edit`, never scope `own` — the server's `sell`.
  /// What `see` is partly made of, and what the sale panel reads.
  sell: boolean
  /// «Прихід»: `stock ≥ edit` (`manage_stock`), never scope `own`.
  receive: boolean
  /// «Списання»: `stock = manage`, never scope `own`.
  writeOff: boolean
  /// «Інвентаризація»: it writes shortages off the shelf — `stock = manage`,
  /// like a write-off, never scope `own` (the server's `requireLevel`).
  count: boolean
  /// New product, price, retire: `stock = manage` with the whole company in
  /// reach — the nomenclature is the company's (403 `company_scope_required`).
  catalog: boolean
  /// A paper on the shelf with no club: the whole company in reach (R8).
  noClub: boolean
  /// «Продажі» (the report) — revenue, cost, margin: a report about money, so
  /// `reports ≥ view` or `finance ≥ view` (the server's 403 otherwise), never
  /// scope `own` — it reads no paper, the report would be empty.
  report: boolean
  /// How a sale line may be priced (specs/shop.md S12).
  prices: PriceRule
}>

/// The price a role may sell a line at — the server's rule (S12), mirrored:
/// a discount under the list up to the role's `maxDiscountPct` (EFFECTIVE —
/// unlimited at `sales: manage`; null = unlimited), and 0 ₴ under a list
/// price only at `sales: manage`.
export type PriceRule = Readonly<{ maxDiscountPct: number | null; givesAway: boolean }>

export const goodsAbilities = (caller: Caller): GoodsAbilities => {
  const level = caller.profile.permissions.stock
  const attached = caller.god || caller.profile.scope !== 'own'
  const costs = caller.god || atLeast(level, 'view')
  const manage = caller.god || level === 'manage'
  const sell = attached && (caller.god || atLeast(caller.profile.permissions.sales, 'edit'))
  return {
    see: costs || sell,
    costs,
    papers: attached && costs,
    sell,
    receive: attached && (caller.god || atLeast(level, 'edit')),
    writeOff: attached && manage,
    count: attached && manage,
    catalog: manage && caller.wholeCompany,
    noClub: attached && caller.wholeCompany,
    report:
      attached &&
      (caller.god || atLeast(caller.profile.permissions.reports, 'view') || atLeast(caller.profile.permissions.finance, 'view')),
    prices: {
      maxDiscountPct: caller.god ? null : effectiveLimits(caller.profile).maxDiscountPct,
      givesAway: caller.god || caller.profile.permissions.sales === 'manage',
    },
  }
}

/// The papers offered on a product's card: a retired product takes no
/// receipt (S9), and a write-off only while it still has goods — its one way
/// out. On a list (no product) the role's abilities as they are.
export const productPapers = (
  can: Pick<GoodsAbilities, 'receive' | 'writeOff'>,
  product?: Pick<Product, 'status' | 'onHand'> | undefined,
): Readonly<{ receive: boolean; writeOff: boolean }> => ({
  receive: can.receive && product?.status !== 'retired',
  writeOff: can.writeOff && !(product?.status === 'retired' && product.onHand <= 0),
})

// ---- the shelf ----

export type StockState = 'ok' | 'low' | 'out' | 'retired'

/// One word for a row: out of stock beats «мало», retired beats both.
export const stockState = (product: Pick<Product, 'onHand' | 'belowMin' | 'status'>): StockState =>
  product.status === 'retired' ? 'retired' : product.onHand <= 0 ? 'out' : product.belowMin ? 'low' : 'ok'

export const STOCK_FILTERS = ['all', 'low', 'out', 'retired'] as const
export type StockFilter = (typeof STOCK_FILTERS)[number]

/// The pills over the list: «Мало» are the ones to reorder (out of stock
/// included — they are the most urgent of them), retired live apart.
export const matchesFilter = (product: Product, filter: StockFilter): boolean => {
  const state = stockState(product)
  switch (filter) {
    case 'all':
      return state !== 'retired'
    case 'low':
      return state === 'low' || state === 'out'
    case 'out':
      return state === 'out'
    case 'retired':
      return state === 'retired'
  }
}

export const countByFilter = (products: readonly Product[]): Record<StockFilter, number> =>
  Object.fromEntries(STOCK_FILTERS.map((f) => [f, products.filter((p) => matchesFilter(p, f)).length])) as Record<
    StockFilter,
    number
  >

/// Name, category or barcode — what a desk types or scans.
export const matchesSearch = (product: Product, query: string): boolean => {
  const q = query.trim().toLocaleLowerCase('uk')
  if (!q) return true
  return [product.name, product.category, product.barcode ?? ''].some((x) => x.toLocaleLowerCase('uk').includes(q))
}

/// What the goods on the shelves would cost to buy again at their LAST
/// purchase prices — an estimate, not a valuation: `lastCostMinor` is a hint
/// for the next receipt (S7), so a pallet bought at 20 ₴ and another at
/// 19,50 ₴ are both counted at 19,50 ₴. Unknown costs count as nothing and
/// are counted apart, so the figure does not pretend. Goods still on a
/// retired product (retired before S9 refused it) are on the shelf all the
/// same: in the sum, and counted apart so somebody writes them off.
export const stockValue = (
  products: readonly Product[],
): Readonly<{ minor: number; unpriced: number; retiredWithStock: number }> =>
  products
    .filter((p) => p.onHand > 0)
    .reduce(
      (acc, p) => ({
        minor: acc.minor + (p.lastCostMinor == null ? 0 : p.onHand * p.lastCostMinor),
        unpriced: acc.unpriced + (p.lastCostMinor == null ? 1 : 0),
        retiredWithStock: acc.retiredWithStock + (p.status === 'retired' ? 1 : 0),
      }),
      { minor: 0, unpriced: 0, retiredWithStock: 0 },
    )

/// The price over the last purchase price, whole percent — as estimated as
/// the cost it is measured against. None without a cost to measure.
export const markupPercent = (priceMinor: number, lastCostMinor: number | null | undefined): number | null =>
  lastCostMinor == null || lastCostMinor <= 0 ? null : Math.round(((priceMinor - lastCostMinor) / lastCostMinor) * 100)

/// The club's own categories, for the combobox — most used first.
export const categoriesOf = (products: readonly Product[]): readonly string[] => {
  const counts = products.reduce((m, p) => m.set(p.category, (m.get(p.category) ?? 0) + 1), new Map<string, number>())
  return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'uk')).map(([name]) => name)
}

/// +24 / −2: the direction of a movement is its kind (S2) — a stocktake's
/// surplus comes in, its shortage goes out.
export const signedQty = (kind: MovementKind, qty: number): number =>
  kind === 'receipt' || kind === 'surplus' ? qty : -qty

// ---- a paper being filled in ----

export type DraftLine = Readonly<{ key: string; productId: string | null; qty: string; unit: string }>

export const emptyLine = (key: string, productId: string | null = null): DraftLine => ({ key, productId, qty: '1', unit: '' })

/// A whole positive count, as the server takes it (1…100 000).
export const parseQty = (text: string): number | null => {
  const cleaned = text.trim()
  if (!/^\d+$/.test(cleaned)) return null
  const n = Number(cleaned)
  return n >= 1 && n <= 100_000 ? n : null
}

/// `discount` — a sale line under the list by more than the role's limit;
/// `zero` — a sale line at 0 ₴ by a role that may not give goods away (S12).
export type LineProblem = 'product' | 'qty' | 'unit' | 'short' | 'discount' | 'zero'

/// What is wrong with one line. `unit` is the receipt's cost or the sale's
/// price: optional (empty = none / the list price), but a number when given.
/// `available` (write-offs, sales) refuses taking more than the shelf holds —
/// summed over every line of the same product, like the server (S3).
export const lineProblems = (
  lines: readonly DraftLine[],
  available?: (productId: string) => number,
): ReadonlyMap<string, LineProblem> => {
  const demand = lines.reduce(
    (m, l) => (l.productId ? m.set(l.productId, (m.get(l.productId) ?? 0) + (parseQty(l.qty) ?? 0)) : m),
    new Map<string, number>(),
  )
  return new Map(
    lines.flatMap((l): [string, LineProblem][] => {
      if (!l.productId) return [[l.key, 'product']]
      if (parseQty(l.qty) == null) return [[l.key, 'qty']]
      if (l.unit.trim() !== '' && parsePrice(l.unit) == null) return [[l.key, 'unit']]
      if (available && (demand.get(l.productId) ?? 0) > available(l.productId)) return [[l.key, 'short']]
      return []
    }),
  )
}

/// Σ qty × unit over the lines that state a unit amount.
export const draftTotal = (lines: readonly DraftLine[], unitOf?: (line: DraftLine) => number | null): number =>
  lines.reduce((sum, l) => {
    const qty = parseQty(l.qty) ?? 0
    const unit = unitOf ? unitOf(l) : l.unit.trim() === '' ? null : parsePrice(l.unit)
    return sum + qty * (unit ?? 0)
  }, 0)

/// The lines a total may count: a line that cannot be posted (more than the
/// shelf holds, no product) is worth nothing yet — «За 369 ₴» for a count the
/// server will refuse is a figure nobody can act on.
export const postableLines = (lines: readonly DraftLine[], problems: ReadonlyMap<string, LineProblem>): readonly DraftLine[] =>
  lines.filter((line) => !problems.has(line.key))

/// What a receipt may take: what is on sale — and what a line already names,
/// so a product retired while the form was open keeps its name on the line
/// and the server says why it cannot come in.
export const receiptCandidates = (products: readonly Product[], lines: readonly DraftLine[]): readonly Product[] =>
  products.filter((p) => p.status === 'active' || lines.some((l) => l.productId === p.id))

/// What a write-off may take: what is on THIS shelf, a retired product's
/// leftovers included — a write-off is their one way out (S9) — and what a
/// line already names.
export const writeOffCandidates = (products: readonly Product[], lines: readonly DraftLine[]): readonly Product[] =>
  products.filter((p) => p.onHand > 0 || lines.some((l) => l.productId === p.id))

/// When a receipt's goods came in, as the server takes it: nothing for
/// today's invoice (the server's clock says when), noon of the invoice's day
/// otherwise — the day is on the paper, the hour nobody wrote down, and noon
/// keeps it inside that day. Never in the future.
export const receivedAt = (date: string, today: string): string | undefined =>
  date === '' || date >= today ? undefined : new Date(`${date}T12:00:00`).toISOString()

export type ReceiptDraft = Readonly<{
  supplier: string
  documentNo: string
  date: string
  note: string
  lines: readonly DraftLine[]
}>

/// A receipt's body from its draft — always in one order of fields, so the
/// same draft is the same paper (domain/paper-attempt.ts `samePaper`).
export const receiptBody = (draft: ReceiptDraft, branchId: string | null, today: string) => {
  const occurredAt = receivedAt(draft.date, today)
  return {
    branchId,
    supplier: draft.supplier.trim() || null,
    documentNo: draft.documentNo.trim() || null,
    note: draft.note.trim() || null,
    ...(occurredAt ? { occurredAt } : {}),
    lines: draft.lines.map((l) => ({
      productId: l.productId as string,
      qty: parseQty(l.qty) as number,
      unitCostMinor: l.unit.trim() === '' ? null : parsePrice(l.unit),
    })),
  }
}

export type WriteOffDraft = Readonly<{ reason: WriteOffReason; note: string; lines: readonly DraftLine[] }>

export const writeOffBody = (draft: WriteOffDraft, branchId: string | null) => ({
  branchId,
  reason: draft.reason,
  note: draft.note.trim() || null,
  lines: draft.lines.map((l) => ({ productId: l.productId as string, qty: parseQty(l.qty) as number })),
})

// ---- a sale ----

/// `mixed` — «Готівка + картка»: part in cash, the rest by card. Not
/// «частинами», which in a Ukrainian shop means instalments.
export type Tender = 'cash' | 'card' | 'mixed'

/// How the money splits: all cash, all card, or the cash typed and the rest
/// on the card. null = the cash part is not a number or is more than the
/// total — the server takes exactly the total (S4), so the panel refuses
/// first.
export const splitTender = (
  tender: Tender,
  totalMinor: number,
  cashText: string,
): Readonly<{ cashMinor: number; cardMinor: number }> | null => {
  if (tender === 'cash') return { cashMinor: totalMinor, cardMinor: 0 }
  if (tender === 'card') return { cashMinor: 0, cardMinor: totalMinor }
  const cash = parsePrice(cashText)
  if (cash == null || cash > totalMinor) return null
  return { cashMinor: cash, cardMinor: totalMinor - cash }
}

/// The price a sale line goes out at: the typed one, else the list.
export const linePrice = (line: DraftLine, product: Pick<Product, 'priceMinor'> | undefined): number | null =>
  line.unit.trim() === '' ? (product?.priceMinor ?? null) : parsePrice(line.unit)

/// Why this role may not sell a line at `priceMinor` against `listMinor` —
/// the server's `priceRefusal` (services/shop utils/stock.ts), to the kopeck,
/// so the panel says it before the server's 403. null = it may.
export const priceProblem = (listMinor: number, priceMinor: number, rule: PriceRule): 'zero' | 'discount' | null => {
  if (priceMinor === 0 && listMinor > 0 && !rule.givesAway) return 'zero'
  const over =
    rule.maxDiscountPct != null && priceMinor < listMinor && (listMinor - priceMinor) * 100 > rule.maxDiscountPct * listMinor
  return over ? 'discount' : null
}

/// How far under the list a price is, in percent to a tenth — «−10 %»,
/// «−6,7 %». null at or above the list (a dearer price is no discount), or
/// with no list to measure against.
export const discountOff = (listMinor: number | null | undefined, priceMinor: number | null | undefined): number | null =>
  listMinor == null || priceMinor == null || listMinor <= 0 || priceMinor >= listMinor
    ? null
    : Math.round(((listMinor - priceMinor) * 1000) / listMinor) / 10

/// 10 → «10», 6.7 → «6,7»: a percentage the Ukrainian way.
export const percentText = (n: number): string => String(n).replace('.', ',')

/// A sale's lines: the shelf's problems first (`lineProblems`), then the
/// price a line goes at against the role's rule (S12).
export const saleLineProblems = (
  lines: readonly DraftLine[],
  products: (productId: string) => Pick<Product, 'onHand' | 'priceMinor'> | undefined,
  rule: PriceRule,
): ReadonlyMap<string, LineProblem> => {
  const shelf = lineProblems(lines, (id) => products(id)?.onHand ?? 0)
  return new Map(
    lines.flatMap((l): [string, LineProblem][] => {
      const known = shelf.get(l.key)
      if (known) return [[l.key, known]]
      const product = l.productId ? products(l.productId) : undefined
      const price = linePrice(l, product)
      const problem = product && price != null ? priceProblem(product.priceMinor, price, rule) : null
      return problem ? [[l.key, problem]] : []
    }),
  )
}

export type SaleDraft = Readonly<{
  clientId: string | null
  cashMinor: number
  cardMinor: number
  lines: readonly DraftLine[]
}>

/// A sale's body from its draft — always in one order of fields, so the same
/// draft is the same paper (domain/paper-attempt.ts `samePaper`). Every line
/// carries the price the panel SHOWED, the list price included: a line sent
/// «at the list» would sell at whatever the list says when it lands, and the
/// desk would have taken money for another sum.
export const saleBody = (
  draft: SaleDraft,
  branchId: string | null,
  products: (productId: string | null) => Pick<Product, 'priceMinor'> | undefined,
) => ({
  branchId,
  clientId: draft.clientId,
  cashMinor: draft.cashMinor,
  cardMinor: draft.cardMinor,
  lines: draft.lines.map((l) => ({
    productId: l.productId as string,
    qty: parseQty(l.qty) as number,
    unitPriceMinor: linePrice(l, products(l.productId)),
  })),
})

export type SaleBody = ReturnType<typeof saleBody>

/// What a sale may take: what is on sale and on THIS shelf — and what a line
/// already names, so a product sold out or retired while the form was open
/// keeps its name on the line and the server says why.
export const saleCandidates = (products: readonly Product[], lines: readonly DraftLine[]): readonly Product[] =>
  products.filter((p) => (p.status === 'active' && p.onHand > 0) || lines.some((l) => l.productId === p.id))

/// What is wrong with the cash part of «Готівка + картка»: nothing typed,
/// not a sum, or more than the sale — the server takes exactly the total
/// (S4). Null when it is fine, and always for all-cash or all-card.
export type CashProblem = 'empty' | 'number' | 'tooMuch'

export const cashProblem = (tender: Tender, totalMinor: number, cashText: string): CashProblem | null => {
  if (tender !== 'mixed') return null
  if (cashText.trim() === '') return 'empty'
  const cash = parsePrice(cashText)
  if (cash == null) return 'number'
  return cash > totalMinor ? 'tooMuch' : null
}

/// The change to hand back: what the client gave in cash over the sale's
/// cash part. The server never sees it — change is the cash desk's (S4) —
/// but the desk counting it out does. `none` — nothing typed.
export type Change =
  | Readonly<{ kind: 'none' }>
  | Readonly<{ kind: 'invalid' }>
  | Readonly<{ kind: 'short'; minor: number }>
  | Readonly<{ kind: 'change'; minor: number }>

export const changeDue = (givenText: string, cashMinor: number): Change => {
  if (givenText.trim() === '') return { kind: 'none' }
  const given = parsePrice(givenText)
  if (given == null) return { kind: 'invalid' }
  return given < cashMinor ? { kind: 'short', minor: cashMinor - given } : { kind: 'change', minor: given - cashMinor }
}

// ---- the report ----

export const PERIODS = ['today', 'week', 'month', 'thisMonth', 'lastMonth'] as const
export type Period = (typeof PERIODS)[number]

// The days of a period and their words are every report's — «Огляд» cuts
// them the same way (domain/periods.ts).
export { periodRange, periodText, type PeriodRange } from './periods'

/// Units sold, per unit — pieces and packs are not one count: «20 шт · 3 уп.».
export const unitsSold = (rows: readonly Pick<SalesFigures & { unit: string }, 'unit' | 'qty'>[]): string =>
  [...rows.reduce((m, row) => m.set(row.unit, (m.get(row.unit) ?? 0) + row.qty), new Map<string, number>())]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'uk'))
    .map(([unit, qty]) => `${qty} ${unit}`)
    .join(' · ')

/// Whose calendar a report is cut by: the chosen club's, else the first club
/// the caller sees, else Kyiv's — the same order as «Послуги»
/// (services-shell.tsx).
export const reportZone = (clubs: readonly Pick<Branch, 'id' | 'timezone'>[], shelf: Shelf): string =>
  reportZoneOf(clubs, shelf.kind === 'club' ? shelf.branchId : null)

/// Margin as a share of revenue, whole percent; none without revenue.
export const marginPercent = (marginMinor: number, revenueMinor: number): number | null =>
  revenueMinor > 0 ? Math.round((marginMinor / revenueMinor) * 100) : null

/// How much of a report row's cost is known (specs/shop.md S13): `none` — no
/// line of it was costed, its cost and margin are unknown, not zero and not
/// the whole price; `part` — some lines were not, the figures are over the
/// rest; `all`.
export type CostKnown = 'none' | 'part' | 'all'

export const costKnown = (row: Pick<SalesFigures, 'uncostedLines' | 'costedRevenueMinor' | 'revenueMinor'>): CostKnown =>
  row.uncostedLines === 0 ? 'all' : row.costedRevenueMinor === 0 && row.revenueMinor > 0 ? 'none' : 'part'

/// A cost or margin figure as the report prints it: «—» when unknown, «≈»
/// before it when any line in it is an estimate.
export const figureText = (
  minor: number,
  row: Pick<SalesFigures, 'uncostedLines' | 'costedRevenueMinor' | 'revenueMinor' | 'estimatedLines'>,
  format: (minor: number) => string,
): string => (costKnown(row) === 'none' ? '—' : `${row.estimatedLines > 0 ? '≈ ' : ''}${format(minor)}`)

/// The margin's share of the revenue it is measured against — the costed
/// revenue, so an uncosted line neither lifts nor sinks it.
export const marginShare = (row: Pick<SalesFigures, 'marginMinor' | 'costedRevenueMinor'>): number | null =>
  marginPercent(row.marginMinor, row.costedRevenueMinor)

/// A key the server dedupes on: the same paper sent twice (a double click, a
/// retry after a dropped connection) is written once (S6). One per open form.
export const newPaperKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
